/**
 * Generic SSE channel plumbing shared by the domain SSE hooks
 * (useWorkOrderSSE, useWorkSessionSSE).
 *
 * Provides:
 * - parseRawSSEEvent: raw `data:` payload -> typed event parsing
 * - GenericWebSSEClient: native EventSource client (cookie auth)
 * - GenericMobileSSEClient: XHR progressive-streaming client (bearer auth)
 * - useSSEChannel: connection lifecycle / state / manual controls hook
 *
 * The two domain hooks have deliberately different error-handling
 * policies (work order SSE stops reconnecting on 401/403, work session
 * SSE keeps retrying) — these divergences are captured explicitly in
 * SSEChannelBehavior rather than silently unified. Backoff timing is
 * identical for both: min(reconnectDelay * 2^(attempt-1), maxReconnectDelay),
 * capped at maxReconnectAttempts.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { isSessionDead } from '../services/mobile/sessionLatch';
import { getMobileAppHeaders } from '../services/mobile/appInfoHeaders';
/**
 * Parse a raw SSE message into a typed event
 */
export function parseRawSSEEvent(rawData, logPrefix = '[SSE]') {
    try {
        const parsed = JSON.parse(rawData);
        if (!parsed.type || !parsed.data) {
            console.warn(`${logPrefix} Invalid event format:`, parsed);
            return null;
        }
        return parsed;
    }
    catch (error) {
        console.error(`${logPrefix} Failed to parse event:`, error, rawData);
        return null;
    }
}
/**
 * Web SSE client using native EventSource.
 * EventSource automatically uses cookies for authentication.
 */
export class GenericWebSSEClient {
    constructor(options, behavior) {
        this.eventSource = null;
        this.state = 'disconnected';
        this.reconnectAttempts = 0;
        this.reconnectTimeout = null;
        this.options = options;
        this.behavior = behavior;
    }
    connect() {
        if (this.eventSource) {
            this.disconnect();
        }
        this.setState('connecting');
        try {
            // Web uses cookies for auth, so no special headers needed
            // EventSource doesn't support custom headers, but credentials are sent via cookies
            this.eventSource = new EventSource(this.options.url, {
                withCredentials: true,
            });
            this.eventSource.onopen = () => {
                this.reconnectAttempts = 0;
                this.setState('connected');
            };
            this.eventSource.onmessage = (event) => {
                const parsedEvent = parseRawSSEEvent(event.data, this.behavior.logPrefix);
                if (parsedEvent) {
                    this.options.onEvent(parsedEvent);
                }
            };
            this.eventSource.onerror = (error) => {
                if (this.behavior.stopOnAuthError) {
                    // EventSource.readyState tells us whether the server rejected the
                    // connection (CLOSED = 2, e.g. 401/403) vs a transient network error
                    // (CONNECTING = 0, browser will auto-reconnect). We must check BEFORE
                    // calling handleError which closes the EventSource.
                    const serverRejected = this.eventSource?.readyState === EventSource.CLOSED;
                    if (serverRejected) {
                        // Server returned a non-200 (typically 401/403). Retrying with the
                        // same credentials will never succeed — stop the reconnect loop and
                        // let the user's next navigation or page reload re-authenticate.
                        console.warn(`${this.behavior.logPrefix} Server rejected connection (likely 401/403). Stopping reconnect.`);
                        this.handleError(new Error('SSE connection rejected by server (auth/permission error)'), false);
                    }
                    else {
                        console.warn(`${this.behavior.logPrefix} Connection error (transient). Will reconnect.`);
                        this.handleError(new Error('SSE connection error'), true);
                    }
                }
                else {
                    console.error(`${this.behavior.logPrefix} Connection error:`, error);
                    this.handleError(new Error('SSE connection error'), true);
                }
            };
        }
        catch (error) {
            this.handleError(error instanceof Error ? error : new Error('Failed to create EventSource'), true);
        }
    }
    disconnect() {
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
        if (this.eventSource) {
            this.eventSource.close();
            this.eventSource = null;
        }
        this.setState('disconnected');
    }
    getState() {
        return this.state;
    }
    isConnected() {
        return this.state === 'connected';
    }
    setState(state) {
        this.state = state;
        this.options.onConnectionStateChange(state);
    }
    handleError(error, shouldRetry) {
        this.options.onError(error);
        this.setState('error');
        // Close existing connection
        if (this.eventSource) {
            this.eventSource.close();
            this.eventSource = null;
        }
        // Only attempt reconnection for transient errors (network blips).
        // Auth errors (401/403) won't resolve by retrying — the user needs to
        // re-authenticate or have their permissions fixed.
        if (shouldRetry && this.options.autoReconnect && this.reconnectAttempts < this.options.maxReconnectAttempts) {
            this.scheduleReconnect();
        }
    }
    scheduleReconnect() {
        this.reconnectAttempts++;
        const delay = Math.min(this.options.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), this.options.maxReconnectDelay);
        if (this.behavior.verboseLogging) {
            console.log(`${this.behavior.logPrefix} Scheduling reconnect attempt ${this.reconnectAttempts} in ${delay}ms`);
        }
        this.reconnectTimeout = setTimeout(() => {
            this.connect();
        }, delay);
    }
}
/**
 * Mobile SSE client — XMLHttpRequest progressive streaming.
 *
 * React Native's fetch has NO streaming body: `response.body` is undefined
 * (and for a never-ending SSE response the fetch promise may never resolve
 * at all), so the previous fetch/getReader implementation could hold the
 * server connection open while the JS side never received a single event —
 * SSE subscriptions piled up server-side and every "realtime" mobile
 * feature silently degraded to its poll fallback. RN's XHR, by contrast,
 * delivers incremental `responseText` via readystatechange/progress events
 * (the same transport react-native-sse uses), which is what this client
 * consumes. Bearer-token auth via the Authorization header.
 */
export class GenericMobileSSEClient {
    constructor(options, behavior) {
        this.xhr = null;
        this.state = 'disconnected';
        this.reconnectAttempts = 0;
        this.reconnectTimeout = null;
        /** Guards against the post-abort readyState 4 callback scheduling a reconnect. */
        this.intentionallyClosed = false;
        this.options = options;
        this.behavior = behavior;
    }
    connect() {
        if (this.xhr) {
            this.disconnect();
        }
        this.intentionallyClosed = false;
        this.setState('connecting');
        this.startXHRStream();
    }
    disconnect() {
        this.intentionallyClosed = true;
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
        if (this.xhr) {
            const xhr = this.xhr;
            this.xhr = null;
            try {
                xhr.abort();
            }
            catch {
                // Aborting an already-settled request throws on some platforms — moot.
            }
        }
        this.setState('disconnected');
    }
    getState() {
        return this.state;
    }
    isConnected() {
        return this.state === 'connected';
    }
    setState(state) {
        this.state = state;
        this.options.onConnectionStateChange(state);
    }
    /**
     * The current token: the live getter when it yields one, else the static
     * snapshot. The getter is consulted first so long-lived channels re-key
     * after a token refresh; the fallback keeps static-token callers (and a
     * getter wrapper that resolves to nothing) working unchanged.
     */
    resolveAuthToken() {
        const fromGetter = this.options.getAuthToken?.();
        return fromGetter ?? this.options.authToken;
    }
    startXHRStream() {
        // The server has refused the session (see sessionLatch): no stream can
        // be authorized until a new sign-in, so none is opened and none retried.
        if (isSessionDead()) {
            this.handleError(new Error('SSE not started: the session is over'), false);
            return;
        }
        const xhr = new XMLHttpRequest();
        this.xhr = xhr;
        const authToken = this.resolveAuthToken();
        this.lastTokenUsed = authToken;
        // Incremental parse state: how far into responseText we've consumed,
        // plus a carry buffer for a line split across two progress events.
        let processedIndex = 0;
        let buffer = '';
        let headersHandled = false;
        const processNewText = () => {
            // responseText is only readable for '' / 'text' response types and
            // grows monotonically as chunks arrive.
            const text = xhr.responseText || '';
            if (text.length <= processedIndex)
                return;
            buffer += text.slice(processedIndex);
            processedIndex = text.length;
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // keep the incomplete trailing line
            for (const line of lines) {
                if (line.startsWith('data: ')) {
                    const event = parseRawSSEEvent(line.slice(6), this.behavior.logPrefix);
                    if (event) {
                        this.options.onEvent(event);
                    }
                }
                // Ignore 'event:', 'id:', 'retry:', and comment lines
            }
        };
        const handleHeaders = () => {
            if (headersHandled)
                return;
            headersHandled = true;
            if (xhr.status >= 200 && xhr.status < 300) {
                this.reconnectAttempts = 0;
                this.setState('connected');
                return;
            }
            const isAuthError = xhr.status === 401 || xhr.status === 403;
            if (isAuthError && isSessionDead()) {
                this.failStream(new Error(`SSE request failed: ${xhr.status} (session over)`), false);
            }
            else if (isAuthError && this.behavior.stopOnAuthError) {
                // Re-key before giving up: if the app refreshed its token since this
                // stream connected, the rejection is about the STALE token, not the
                // session — retry with the fresh one instead of halting the channel.
                const freshToken = this.resolveAuthToken();
                if (freshToken && freshToken !== this.lastTokenUsed) {
                    console.warn(`${this.behavior.logPrefix} Server returned ${xhr.status} for a rotated token. Re-keying and retrying.`);
                    this.failStream(new Error(`SSE request failed: ${xhr.status} (token rotated; re-keying)`), true);
                    return;
                }
                console.warn(`${this.behavior.logPrefix} Server returned ${xhr.status}. Stopping reconnect.`);
                this.failStream(new Error(`SSE request failed: ${xhr.status}`), false);
            }
            else if (xhr.status === 403) {
                // For 403 errors, suggest that the auth token may need refreshing
                console.error(`${this.behavior.logPrefix} 403 Forbidden - auth token:`, this.lastTokenUsed ? 'present' : 'missing');
                this.failStream(new Error('SSE request failed: 403 - Authentication token may have expired or is missing. Please try logging in again.'), true);
            }
            else {
                this.failStream(new Error(`SSE request failed: ${xhr.status}`), true);
            }
        };
        xhr.onreadystatechange = () => {
            if (this.intentionallyClosed || this.xhr !== xhr)
                return;
            if (xhr.readyState === XMLHttpRequest.HEADERS_RECEIVED) {
                handleHeaders();
            }
            else if (xhr.readyState === XMLHttpRequest.LOADING) {
                handleHeaders();
                if (this.state === 'connected')
                    processNewText();
            }
            else if (xhr.readyState === XMLHttpRequest.DONE) {
                handleHeaders();
                if (this.state === 'connected') {
                    processNewText();
                    if (this.behavior.verboseLogging) {
                        console.log(`${this.behavior.logPrefix} Stream ended`);
                    }
                    // A finished SSE response is a dropped connection — reconnect.
                    this.failStream(new Error('Stream ended unexpectedly'), true);
                }
            }
        };
        // Some RN versions surface incremental text via progress events rather
        // than repeated LOADING readystatechanges; consume both (processNewText
        // is idempotent over the processedIndex watermark).
        xhr.onprogress = () => {
            if (this.intentionallyClosed || this.xhr !== xhr)
                return;
            handleHeaders();
            if (this.state === 'connected')
                processNewText();
        };
        xhr.onerror = () => {
            if (this.intentionallyClosed || this.xhr !== xhr)
                return;
            this.failStream(new Error('SSE network error'), true);
        };
        try {
            xhr.open('GET', this.options.url);
            xhr.responseType = ''; // text — required for readable responseText
            xhr.setRequestHeader('Accept', 'text/event-stream');
            xhr.setRequestHeader('Cache-Control', 'no-cache');
            for (const [name, value] of Object.entries(getMobileAppHeaders())) {
                xhr.setRequestHeader(name, value);
            }
            if (authToken) {
                xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
            }
            else if (this.behavior.warnOnMissingAuthToken) {
                console.warn(`${this.behavior.logPrefix} No auth token provided for SSE connection`);
            }
            xhr.send();
        }
        catch (error) {
            this.failStream(error instanceof Error ? error : new Error('SSE request failed to start'), true);
        }
    }
    /** Tear down the current request and route through error/reconnect handling. */
    failStream(error, shouldRetry) {
        if (this.xhr) {
            const xhr = this.xhr;
            this.xhr = null;
            try {
                xhr.abort();
            }
            catch {
                // Already settled — moot.
            }
        }
        this.handleError(error, shouldRetry);
    }
    handleError(error, shouldRetry) {
        this.options.onError(error);
        this.setState('error');
        if (shouldRetry && this.options.autoReconnect && this.reconnectAttempts < this.options.maxReconnectAttempts) {
            this.scheduleReconnect();
        }
    }
    scheduleReconnect() {
        this.reconnectAttempts++;
        const delay = Math.min(this.options.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), this.options.maxReconnectDelay);
        if (this.behavior.verboseLogging) {
            console.log(`${this.behavior.logPrefix} Scheduling reconnect attempt ${this.reconnectAttempts} in ${delay}ms`);
        }
        this.reconnectTimeout = setTimeout(() => {
            this.intentionallyClosed = false;
            this.setState('connecting');
            this.startXHRStream();
        }, delay);
    }
}
export function isReactNative() {
    return typeof navigator !== 'undefined' && navigator.product === 'ReactNative';
}
/**
 * Generic hook managing an SSE channel's connection lifecycle:
 * client creation, connect on mount / option change, cleanup on unmount,
 * connection state + last event tracking, and manual reconnect/disconnect.
 *
 * Consumer callbacks are kept in refs so that passing inline functions
 * does not tear down and recreate the connection on every render.
 */
export function useSSEChannel(options) {
    const { url, authToken, getAuthToken, enabled = true, onEvent, onError, onConnectionStateChange, autoReconnect = true, reconnectDelay = 1000, maxReconnectDelay = 30000, maxReconnectAttempts = 10, createClient, hookLabel = '[useSSEChannel]', } = options;
    const [connectionState, setConnectionState] = useState('disconnected');
    const [lastEvent, setLastEvent] = useState(null);
    const [reconnectAttempts, setReconnectAttempts] = useState(0);
    const clientRef = useRef(null);
    // Use refs to avoid stale closures in callbacks
    const callbacksRef = useRef({ onEvent, onError, onConnectionStateChange });
    // Update refs when callbacks change
    useEffect(() => {
        callbacksRef.current = { onEvent, onError, onConnectionStateChange };
    }, [onEvent, onError, onConnectionStateChange]);
    // Latest-ref for the token getter: callers pass inline closures, and a new
    // identity per render must not tear down and rebuild the connection. The
    // stable wrapper below is what the client holds; it always reads current.
    const getAuthTokenRef = useRef(getAuthToken);
    useEffect(() => {
        getAuthTokenRef.current = getAuthToken;
    }, [getAuthToken]);
    const stableGetAuthToken = useCallback(() => getAuthTokenRef.current?.(), []);
    // Handle incoming events
    const handleEvent = useCallback((event) => {
        setLastEvent(event);
        callbacksRef.current.onEvent?.(event);
    }, []);
    // Handle connection state changes
    const handleConnectionStateChange = useCallback((state) => {
        setConnectionState(state);
        callbacksRef.current.onConnectionStateChange?.(state);
    }, []);
    // Handle errors
    const handleError = useCallback((error) => {
        console.error(`${hookLabel} Error:`, error);
        callbacksRef.current.onError?.(error);
        setReconnectAttempts((prev) => prev + 1);
    }, [hookLabel]);
    // Create and connect client
    useEffect(() => {
        if (!enabled || !url) {
            return;
        }
        const client = createClient({
            url,
            authToken,
            getAuthToken: stableGetAuthToken,
            onEvent: handleEvent,
            onConnectionStateChange: handleConnectionStateChange,
            onError: handleError,
            autoReconnect,
            reconnectDelay,
            maxReconnectDelay,
            maxReconnectAttempts,
        });
        clientRef.current = client;
        client.connect();
        return () => {
            client.disconnect();
            clientRef.current = null;
        };
    }, [
        enabled,
        url,
        authToken,
        stableGetAuthToken,
        createClient,
        handleEvent,
        handleConnectionStateChange,
        handleError,
        autoReconnect,
        reconnectDelay,
        maxReconnectDelay,
        maxReconnectAttempts,
    ]);
    // Manual reconnect function
    const reconnect = useCallback(() => {
        if (clientRef.current) {
            setReconnectAttempts(0);
            clientRef.current.disconnect();
            clientRef.current.connect();
        }
    }, []);
    // Manual disconnect function
    const disconnect = useCallback(() => {
        if (clientRef.current) {
            clientRef.current.disconnect();
        }
    }, []);
    return {
        connectionState,
        lastEvent,
        isConnected: connectionState === 'connected',
        reconnect,
        disconnect,
        reconnectAttempts,
    };
}
